/*!
 * vue-role-manager v1.2.0
 * A Vue.js plugin that manage user roles. Including routes protect, vue directive.
 * Authors: neikvon
 * Date:    2019-05-27 18:14:15
*/
var isBoolean = function (value) { return typeof value === 'boolean'; };
var isArray = function (value) { return Array.isArray(value); };
var isJson = function (obj) {
  try {
    return obj.constructor === {}.constructor
  } catch (err) {}
  return false
};
var isValidRoleName = function (name) { return typeof name === 'string' || !isNaN(name); };

var Manager = function Manager (
  Vue,
  ref
) {
  if ( ref === void 0 ) ref = {};
  var router = ref.router; if ( router === void 0 ) router = null;
  var redirect = ref.redirect; if ( redirect === void 0 ) redirect = 'login';
  var metaName = ref.metaName; if ( metaName === void 0 ) metaName = 'roles';
  var whitelist = ref.whitelist; if ( whitelist === void 0 ) whitelist = [];
  var debug = ref.debug; if ( debug === void 0 ) debug = process.env.NODE_ENV === 'development';
  var enabled = ref.enabled; if ( enabled === void 0 ) enabled = true;

  this.router = router;
  this.defaultRedirect = redirect;
  this.metaName = metaName;
  this.whitelist = isArray(whitelist) ? whitelist : [whitelist];
  this.debug = debug;
  this.enabled = enabled;
  if (this.defaultRedirect) {
    this.whitelist.push(this.defaultRedirect);
  }

  this.vm = new Vue({
    data: {
      userRoles: []
    }
  });

  this.allRoutes = this.router.options.routes || [];
  this.tmpRoute = null;
};

/**
 * get current user's roles
 *
 * @returns
 * @memberof Manager
 */
Manager.prototype.getRoles = function getRoles () {
  if (!isArray(this.vm.userRoles) || this.vm.userRoles.length < 1) {
    throw new Error('Attempt to access user roles before being set')
  }
  return this.vm.userRoles
};

/**
 * set current user's roles
 *
 * @param {*} roles
 * @memberof Manager
 */
Manager.prototype.setRoles = function setRoles (roles) {
  this.vm.userRoles = roles ? (isArray(roles) ? roles : [roles]) : [];
  if (this.to && this.router) {
    var ref = this.hasAccessToRoute(this.to);
      var access = ref.access;
      var redirect = ref.redirect;
    if (!access) {
      this.router.push({
        name: redirect,
        query: {
          redirect: this.to.path
        }
      });
    }
  }
};

/**
 * check current user's roles
 *
 * @param {*} roles roles to check
 * @returns
 * @memberof Manager
 */
Manager.prototype.hasAccess = function hasAccess (roles) {
  if (!roles) {
    throw new Error('Param is required')
  }
  var _roles = isArray(roles) ? roles : [roles];
  return this.vm.userRoles.some(function (role) { return _roles.includes(role); })
};

/**
 * add routes
 * addRoutes([routesConfig])
 * addRoutes([routesConfig], '/parent')
 * addRoutes([routesConfig], 'parent')
 * addRoutes([routesConfig], { path: '/parent' })
 * addRoutes([routesConfig], { name: 'parent' })
 * @param {Array} routes
 * @param {String|Object} parent
 * @returns
 * @memberof Manager
 */
Manager.prototype.addRoutes = function addRoutes (routes, parent) {
  if (!routes || routes.length < 1) {
    return {
      allRoutes: this.allRoutes
    }
  }

  var matchedRoute;
  if (parent) {
    var _parent = parent;
    if (typeof parent === 'string') {
      _parent = parent.startsWith('/')
        ? {
          path: parent
        }
        : {
          name: parent
        };
    }
    matchedRoute = _parent.name
      ? this.findRouteByName(_parent.name)
      : this.allRoutes.find(function (r) { return r.path === _parent.path; });

    if (!matchedRoute) {
      return {
        allRoutes: this.allRoutes
      }
    }
  }

  var _routes = isArray(routes) ? routes : [routes];
  var allowdRoutes = this.enabled ? this._routesFilterByRole(_routes, true) : _routes;

  if (!allowdRoutes || allowdRoutes.length < 0) {
    return {
      allRoutes: this.allRoutes
    }
  }

  if (parent) {
    matchedRoute.children = matchedRoute.children || [];
    matchedRoute.children = matchedRoute.children.concat(allowdRoutes);
  } else {
    matchedRoute = allowdRoutes;
    this.allRoutes = this.allRoutes.concat(allowdRoutes);
  }

  this.router.addRoutes(isArray(matchedRoute) ? matchedRoute : [matchedRoute], {
    replace: true
  });

  return {
    addedRoutes: matchedRoute,
    allRoutes: this.allRoutes
  }
};

/**
 * resolve route
 *
 * @param {*} to
 * @param {*} from
 * @param {*} next
 * @memberof Manager
 */
Manager.prototype.resolve = function resolve (to, from, next) {
  this.to = to;
  var ref = this.hasAccessToRoute(to);
    var access = ref.access;
    var redirect = ref.redirect;
    var skipped = ref.skipped;

  if (this.debug && !skipped) {
    console.log(
      ("[VRM] " + (access ? 'ALLOW' : 'DENY') + " \"" + (to.fullPath) + "\" from roles \"" + (this.vm.userRoles.join(
        ','
      )) + "\"."),
      !access ? ("Redirect to " + redirect + ".") : ''
    );
  }

  access
    ? next()
    : next({
      name: redirect,
      query: {
        redirect: to.path
      }
    });
};

/**
 * check if the current user has access to a specified route
 *
 * @param {*} route string|route object
 * @param {*} isFilter
 * @returns
 * @memberof Manager
 */
Manager.prototype.hasAccessToRoute = function hasAccessToRoute (route, isFilter) {
    var this$1 = this;

  var currentRoute = typeof route === 'string' ? this.router.resolve(route).route : route;

  if (this.whitelist.includes(currentRoute.name)) {
    if (this.debug) {
      console.log('[VRM] skipped:', currentRoute.name);
    }
    return {
      access: true,
      skipped: true
    }
  }

  var params = currentRoute.params;
  var query = currentRoute.query;
  var currentConfig = this._resolveConfigs(currentRoute, params, query);
  var currentAccess = this._resolvePermission(currentConfig);

  if (this.debug) {
    console.log('[VRM] configs:', currentConfig);
  }

  if (!currentAccess) {
    if (this.debug && isFilter) {
      console.log('[VRM] filter:', currentRoute.path, 'DENY');
    }
    return {
      access: false,
      redirect: currentConfig.redirect
    }
  }

  if (!currentConfig.inherit) {
    if (this.debug && isFilter) {
      console.log('[VRM] filter:', currentRoute.path, currentAccess ? 'ALLOW' : 'DENY');
    }
    return {
      access: currentAccess,
      redirect: currentConfig.redirect
    }
  }

  var parentRoutes = currentRoute.matched
    ? currentRoute.matched.slice(0, currentRoute.matched.length - 1)
    : [];
  var result;

  for (var i = parentRoutes.length - 1; i >= 0; i--) {
    var parent = parentRoutes[i];

    var configs = this$1._resolveConfigs(parent, params, query);
    if (!configs) {
      break
    }

    if (this$1.debug) {
      console.log('[VRM] configs:', configs);
    }

    var access = this$1._resolvePermission(configs);
    result = {
      access: access,
      redirect: configs.redirect
    };
    if (configs.strip || !access) {
      break
    }
  }

  if (this.debug && isFilter) {
    console.log('[VRM] filter:', currentRoute.path, result ? 'DENY' : 'ALLOW');
  }

  return (
    result || {
      access: true
    }
  )
};

/**
 * find the route by route name
 *
 * @param {*} name
 * @param {*} [routes=this.allRoutes]
 * @returns
 * @memberof Manager
 */
Manager.prototype.findRouteByName = function findRouteByName (name, routes) {
    var this$1 = this;
    if ( routes === void 0 ) routes = this.allRoutes;

  if (this.tmpRoute) {
    this.tmpRoute = null;
  }
  if (!name || !isArray(routes)) {
    return null
  }

  for (var i = 0, len = routes.length; i < len; i++) {
    var item = routes[i];
    if (item.name === name) {
      this$1.tmpRoute = item;
    } else if (isArray(item.children)) {
      this$1.findRouteByName(name, item.children);
    }
    if (this$1.tmpRoute) {
      break
    }
  }

  return this.tmpRoute
};

/**
 * resolve configs
 *
 m: (user, route, params, query) => {
   return array|object|boolean
 }
 m: {
   allow: (user, route, params, query) => {
      return array|object|boolean
   }
 }
 m: {
   deny: (user, route, params, query) => {
      return array|object|boolean
   }
 }

 m: 'role1'|1

 m: ['role1', 'role2']

 m: {
   allow: 'role1',
   deny: 'role3',
   redirect: ''
 }

 m: {
   allow: ['role1', 'role2'],
   deny: ['role3']
 }

 * @param {*} route
 * @returns
 * @memberof Manager
 */
Manager.prototype._resolveConfigs = function _resolveConfigs (route, params, query) {
  var configs = {
    allow: null,
    deny: null,
    redirect: this.defaultRedirect,
    inherit: true
  };

  if (!route.meta || !route.meta[this.metaName]) {
    return configs
  }

  var meta = route.meta[this.metaName];

  if (typeof meta === 'function') {
    var result = meta(this.vm.userRoles, route, params, query);
    if (isBoolean(result)) {
      configs[result ? 'allow' : 'deny'] = true;

      return configs
    }

    if (isArray(result)) {
      return Object.assign({}, configs, {
        allow: result
      })
    }

    return Object.assign({}, configs, result, {
      allow:
        isArray(result.allow) || isBoolean(result.allow) || !result.allow
          ? result.allow
          : [result.allow],
      deny:
        isArray(result.deny) || isBoolean(result.deny) || !result.deny
          ? result.deny
          : [result.deny]
    })
  }

  if (meta.redirect) {
    configs.redirect = meta.redirect;
  }

  if (isValidRoleName(meta)) {
    configs.allow = [meta];
    return configs
  }

  if (isArray(meta)) {
    configs.allow = meta.filter(isValidRoleName);
    return configs
  }

  if (isJson(meta)) {
    if (meta.allow) {
      if (isArray(meta.allow)) {
        configs.allow = meta.allow.filter(isValidRoleName);
      } else if (typeof meta.allow === 'function') {
        var _meta = meta.allow(this.vm.userRoles, route, params, query);
        configs.allow = isArray(_meta) || isBoolean(_meta) || !_meta ? _meta : [_meta];
      } else if (isValidRoleName(meta.allow)) {
        configs.allow = [meta.allow];
      }
    }
    if (meta.deny) {
      if (isArray(meta.deny)) {
        configs.deny = meta.deny.filter(isValidRoleName);
      } else if (typeof meta.deny === 'function') {
        var _meta$1 = meta.deny(this.vm.userRoles, route, params, query);
        configs.deny = isArray(_meta$1) || isBoolean(_meta$1) || !_meta$1 ? _meta$1 : [_meta$1];
      } else if (isValidRoleName(meta.deny)) {
        configs.deny = [meta.deny];
      }
    }
  }

  return configs
};

Manager.prototype._resolvePermission = function _resolvePermission (configs) {
  // deny
  if (isBoolean(configs.deny) && configs.deny) {
    return false
  } else if (isArray(configs.deny) && this.vm.userRoles.some(function (r) { return configs.deny.includes(r); })) {
    return false
  }

  // allow
  if (isBoolean(configs.allow) && !configs.allow) {
    return false
  } else if (isArray(configs.allow) && !this.vm.userRoles.some(function (r) { return configs.allow.includes(r); })) {
    return false
  }

  return true
};

Manager.prototype._routesFilterByRole = function _routesFilterByRole (routes, isFilter) {
    var this$1 = this;

  var allowedRoutes = routes.filter(function (route) {
    var tmp = this$1.hasAccessToRoute(route, isFilter);
    if (tmp.access) {
      if (route.children && route.children.length) {
        route.children = this$1._routesFilterByRole(route.children, isFilter);
      }
      return true
    }
    return false
  });
  return allowedRoutes
};

function directive (manager) { return ({
  inserted: handler.bind(manager)
}); }

function handler (el, binding) {
  var hasAccess;
  if (typeof binding.value === 'function') {
    hasAccess = binding.value();
  } else {
    hasAccess = this.hasAccess(binding.value);
  }

  if (hasAccess) {
    return
  }

  /*
    arg: action, class

    modifiers:
    action => remove, hidden, disable
    class => any string
    */
  var arg = binding.arg;

  // modifiers: remove hidden disable
  var modifiers = binding.modifiers
    ? binding.modifiers
    : {
      remove: true
    };

  if (arg === 'action') {
    if (modifiers.remove) {
      el.parentElement && el.parentElement.removeChild(el);
    } else if (modifiers.hidden) {
      el.style.display = 'none';
    } else if (modifiers.disable) {
      el.disabled = true;
    }
  } else if (arg === 'class') {
    Object.keys(binding.modifiers).map(function (cls) { return el.classList.contains(cls) ? null : (el.className += ' ' + cls); }
    );
  } else {
    el.parentElement && el.parentElement.removeChild(el);
  }
}

var version = "1.2.0";

function plugin (Vue, opts) {
  var manager = new Manager(Vue, opts);

  // router hook
  if (opts.router && opts.enabled !== false) {
    opts.router.beforeEach(function (to, from, next) { return manager.resolve(to, from, next); });
  }

  // prototype
  Vue.prototype.$vrm = manager;

  // directive
  Vue.directive('roles', directive(manager));
}

plugin.version = version;

if (typeof window !== 'undefined' && window.Vue) {
  window.Vue.use(plugin);
}

export default plugin;
//# sourceMappingURL=vue-role-manager.esm.js.map
